import { useState } from 'react';
import { orders as allOrders } from '@/mocks/orders';
import type { Order } from '@/mocks/orders';
import { statusLabels } from '@/mocks/orders';

const cargoStatuses = [
  { key: 'bekliyor', label: 'Kargo Bekliyor', color: 'bg-stone-400' },
  { key: 'hazir', label: 'Kargoya Hazır', color: 'bg-status-navy' },
  { key: 'verildi', label: 'Kargoya Verildi', color: 'bg-status-sky' },
  { key: 'yolda', label: 'Yolda', color: 'bg-status-blue' },
  { key: 'dagitimda', label: 'Dağıtımda', color: 'bg-status-purple' },
  { key: 'teslim_edildi', label: 'Teslim Edildi', color: 'bg-status-green' },
  { key: 'teslim_edilemedi', label: 'Teslim Edilemedi', color: 'bg-status-orange' },
  { key: 'iade_surecinde', label: 'İade Sürecinde', color: 'bg-status-red' },
  { key: 'iade_geldi', label: 'İade Geldi', color: 'bg-status-gray' },
];

function getCargoColor(status: string | null): string {
  const found = cargoStatuses.find(s => s.key === status);
  return found?.color || 'bg-stone-300';
}

function getCargoLabel(status: string | null): string {
  const found = cargoStatuses.find(s => s.key === status);
  return found?.label || 'Belirsiz';
}

export default function CargoPage() {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showAddTracking, setShowAddTracking] = useState<string | null>(null);

  let filtered = allOrders.filter(o =>
    o.cargoStatus !== null || o.status === 'kargoya_hazir' || o.status === 'kargoda' || o.status === 'teslim'
  );

  if (search) {
    const q = search.toLowerCase();
    filtered = filtered.filter(o =>
      o.customerName.toLowerCase().includes(q) ||
      o.id.toLowerCase().includes(q) ||
      (o.cargoTrackingNo && o.cargoTrackingNo.toLowerCase().includes(q)) ||
      (o.cargoCompany && o.cargoCompany.toLowerCase().includes(q)) ||
      o.city.toLowerCase().includes(q)
    );
  }

  if (statusFilter) {
    filtered = filtered.filter(o => o.cargoStatus === statusFilter);
  }

  const totalCount = filtered.length;
  const deliveredCount = filtered.filter(o => o.cargoStatus === 'teslim_edildi').length;
  const inTransitCount = filtered.filter(o => o.cargoStatus === 'yolda' || o.cargoStatus === 'dagitimda').length;
  const pendingCount = filtered.filter(o => o.cargoStatus === 'hazir' || o.cargoStatus === 'bekliyor' || !o.cargoStatus).length;
  const problemCount = filtered.filter(o => o.cargoStatus === 'teslim_edilemedi' || o.cargoStatus === 'iade_surecinde' || o.cargoStatus === 'iade_geldi').length;

  const getCargoMessage = (order: Order) => {
    return `Merhaba ${order.customerName}, Tablocum.11 siparişiniz kargoya verilmiştir.\nKargo firması: ${order.cargoCompany || '-'}\nTakip numarası: ${order.cargoTrackingNo || '-'}\nÜrün teslim sürecinizi bu takip numarası ile kontrol edebilirsiniz.`;
  };

  return (
    <div>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
        <div>
          <h2 className="text-xl md:text-2xl font-bold text-stone-900">Kargo Takibi</h2>
          <p className="text-sm text-stone-500 mt-0.5">Tüm gönderileri tek yerden takip edin</p>
        </div>
        <button className="flex items-center gap-2 px-4 py-2 bg-gold-500 text-white text-sm font-medium rounded-lg hover:bg-gold-600 transition-colors cursor-pointer whitespace-nowrap">
          <i className="ri-add-line" />Toplu Kargo Ekle
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
        <div className="bg-white rounded-xl border border-stone-200/80 p-4">
          <p className="text-2xl font-bold text-stone-900">{totalCount}</p>
          <p className="text-xs text-stone-500 mt-1">Toplam Gönderi</p>
        </div>
        <div className="bg-white rounded-xl border border-stone-200/80 p-4">
          <p className="text-2xl font-bold text-status-green">{deliveredCount}</p>
          <p className="text-xs text-stone-500 mt-1">Teslim Edilen</p>
        </div>
        <div className="bg-white rounded-xl border border-stone-200/80 p-4">
          <p className="text-2xl font-bold text-status-blue">{inTransitCount}</p>
          <p className="text-xs text-stone-500 mt-1">Yolda / Dağıtımda</p>
        </div>
        <div className="bg-white rounded-xl border border-stone-200/80 p-4">
          <p className="text-2xl font-bold text-status-orange">{pendingCount + problemCount}</p>
          <p className="text-xs text-stone-500 mt-1">Bekleyen / Sorunlu</p>
        </div>
      </div>

      {/* Search & Filter */}
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-stone-400 text-sm" />
          <input
            type="text"
            placeholder="Müşteri, sipariş ID, takip no veya şehir ile ara..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-4 py-2.5 bg-white border border-stone-200 rounded-lg text-sm text-stone-800 placeholder:text-stone-400 focus:outline-none focus:border-gold-400 focus:ring-1 focus:ring-gold-400/30"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-3 py-2.5 bg-white border border-stone-200 rounded-lg text-sm text-stone-700 cursor-pointer focus:outline-none focus:border-gold-400"
        >
          <option value="">Tüm Kargo Durumları</option>
          {cargoStatuses.map((s) => (
            <option key={s.key} value={s.key}>{s.label}</option>
          ))}
        </select>
      </div>

      {/* Cargo Table */}
      <div className="bg-white rounded-xl border border-stone-200/80 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-stone-100 bg-stone-50/50">
                <th className="text-left text-xs font-semibold text-stone-500 px-4 py-3 whitespace-nowrap">Sipariş ID</th>
                <th className="text-left text-xs font-semibold text-stone-500 px-4 py-3 whitespace-nowrap">Müşteri</th>
                <th className="text-left text-xs font-semibold text-stone-500 px-4 py-3 whitespace-nowrap">Telefon</th>
                <th className="text-left text-xs font-semibold text-stone-500 px-4 py-3 whitespace-nowrap">İl</th>
                <th className="text-left text-xs font-semibold text-stone-500 px-4 py-3 whitespace-nowrap">Kargo Firması</th>
                <th className="text-left text-xs font-semibold text-stone-500 px-4 py-3 whitespace-nowrap">Takip No</th>
                <th className="text-left text-xs font-semibold text-stone-500 px-4 py-3 whitespace-nowrap">Kargo Durumu</th>
                <th className="text-left text-xs font-semibold text-stone-500 px-4 py-3 whitespace-nowrap">Tarih</th>
                <th className="text-right text-xs font-semibold text-stone-500 px-4 py-3 whitespace-nowrap">Tutar</th>
                <th className="text-center text-xs font-semibold text-stone-500 px-4 py-3 whitespace-nowrap">Aksiyon</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-50">
              {filtered.map((order) => (
                <tr key={order.id} className="hover:bg-stone-50/50 transition-colors group">
                  <td className="px-4 py-3">
                    <span className="text-xs font-mono font-semibold text-stone-800">{order.id}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm font-medium text-stone-800">{order.customerName}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-xs text-stone-500 whitespace-nowrap">{order.phone.replace('90', '0')}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-xs text-stone-500">{order.city}/{order.district}</span>
                  </td>
                  <td className="px-4 py-3">
                    {order.cargoCompany ? (
                      <span className="text-xs text-stone-700">{order.cargoCompany}</span>
                    ) : (
                      <>
                        {order.status === 'kargoya_hazir' ? (
                          <button
                            onClick={() => setShowAddTracking(order.id)}
                            className="text-xs text-gold-600 hover:text-gold-700 font-medium cursor-pointer"
                          >
                            + Ekle
                          </button>
                        ) : (
                          <span className="text-xs text-stone-300">-</span>
                        )}
                      </>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {order.cargoTrackingNo ? (
                      <span className="text-xs font-mono text-stone-700">{order.cargoTrackingNo}</span>
                    ) : (
                      <span className="text-xs text-stone-300">-</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium ${
                      order.cargoStatus === 'teslim_edildi' ? 'bg-status-green/10 text-status-green' :
                      order.cargoStatus === 'yolda' || order.cargoStatus === 'dagitimda' ? 'bg-status-blue/10 text-status-blue' :
                      order.cargoStatus === 'teslim_edilemedi' || order.cargoStatus === 'iade_surecinde' ? 'bg-status-red/10 text-status-red' :
                      order.cargoStatus === 'iade_geldi' ? 'bg-status-gray/10 text-status-gray' :
                      order.cargoStatus === 'hazir' ? 'bg-status-navy/10 text-status-navy' :
                      'bg-status-orange/10 text-status-orange'
                    }`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${getCargoColor(order.cargoStatus)}`} />
                      {getCargoLabel(order.cargoStatus)}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-xs text-stone-500 whitespace-nowrap">{order.cargoDate || order.orderDate}</span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div>
                      <span className="text-sm font-semibold text-stone-800">{order.amount.toLocaleString('tr-TR')} ₺</span>
                      {order.paymentType === 'kapida_odeme' && order.paymentStatus === 'bekleniyor' && (
                        <span className="block text-[10px] text-status-orange font-medium">Tahsilat bekleniyor</span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-center gap-1">
                      {(!order.cargoTrackingNo && order.status === 'kargoya_hazir') && (
                        <button
                          onClick={() => setShowAddTracking(order.id)}
                          className="w-7 h-7 flex items-center justify-center rounded-md bg-gold-50 text-gold-600 hover:bg-gold-100 transition-colors cursor-pointer"
                          title="Takip No Ekle"
                        >
                          <i className="ri-add-line text-sm" />
                        </button>
                      )}
                      {order.cargoTrackingNo && (
                        <a
                          href={`https://wa.me/${order.phone}?text=${encodeURIComponent(getCargoMessage(order))}`}
                          target="_blank"
                          rel="nofollow noopener noreferrer"
                          className="w-7 h-7 flex items-center justify-center rounded-md bg-green-50 text-green-600 hover:bg-green-100 transition-colors cursor-pointer"
                          title="Kargo Mesajı Gönder"
                        >
                          <i className="ri-whatsapp-line text-sm" />
                        </a>
                      )}
                      {order.cargoStatus && order.cargoStatus !== 'teslim_edildi' && order.cargoStatus !== 'iade_geldi' && (
                        <button
                          className="w-7 h-7 flex items-center justify-center rounded-md bg-status-green/10 text-status-green hover:bg-status-green/20 transition-colors cursor-pointer"
                          title="Teslim Edildi"
                        >
                          <i className="ri-check-line text-sm" />
                        </button>
                      )}
                      {order.cargoStatus && order.cargoStatus !== 'iade_surecinde' && order.cargoStatus !== 'iade_geldi' && order.cargoStatus !== 'teslim_edildi' && (
                        <button
                          className="w-7 h-7 flex items-center justify-center rounded-md bg-status-red/10 text-status-red hover:bg-status-red/20 transition-colors cursor-pointer"
                          title="İade İşaretle"
                        >
                          <i className="ri-arrow-go-back-line text-sm" />
                        </button>
                      )}
                      <button
                        className="w-7 h-7 flex items-center justify-center rounded-md bg-stone-100 text-stone-500 hover:bg-stone-200 transition-colors cursor-pointer"
                        title="Detay"
                      >
                        <i className="ri-more-line text-sm" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}